import React from "react";
import { CardProps } from "./card.types";
import {
  layoutVariants,
  imageVariants,
  titleVariants,
  descriptionVariants,
  titleSizeVariants,
  descriptionSizeVariants,
  hoverVariants,
  titleMarginVariants,
  descriptionLineClampVariants,
  titleLineClampVariants,
} from "./cardVariants";
import Image from "next/image";
import { shortenText } from "@/services/commonFunction";
import Link from "next/link";
import { TimeBadge } from "@/components/ui/CustomBadge";
import { Play } from "lucide-react";
import GumletImage from "@/utils/GumletSrcSet";
// import Modal from "../Modal/Modal";
// import { Breadcrumb } from "../Breadcrumb";
// import { BreadcrumbData } from "@/types/common";

export const Card = ({
  data,
  title = "default",
  description = "default",
  titleSize = "large",
  descriptionSize = "xlarge",
  descriptionLineClamp = "default",
  titleLineClamp = "default",
  imageStyle = "default",
  layout = "default",
  textOnImage = false,
  className = "",
  hoverStyle = "default",
  showDuration = false,
  durationPosition = "top-right",
  isVideo = "0",
  titleMargin="default",
  hideImage = false,
  hideDescription = false,
  hideBrandLogo,
  priority = false,
  sizes = "100vw",
}: CardProps) => {

  // Position class mapping
  const positionClasses = {
    "top-left": "top-2 left-2",
    "top-right": "top-2 right-2",
    "bottom-left": "bottom-2 left-2",
    "bottom-right": "bottom-2 right-2",
  };

  // const [isModalOpen, setIsModalOpen] = useState(false);

  //  const handleCardClick = (e: React.MouseEvent) => {
  //     if (isVideo) {
  //       e.preventDefault();
  //       setIsModalOpen(true);
  //     }
  //   };

  // const handleCloseModal = () => {
  //   setIsModalOpen(false);
  // };
  // console.log("priority-card", priority);


  return (
    <>
    <div
      className={`overflow-hidden ${layoutVariants[layout]} group ${className}`}
    >
      {/* Image container - always has position relative for badge positioning */}
      <div className="relative overflow-hidden">
        {!hideImage && layout === "horizontal" && (
          <div className="h-32 w-48 flex-shrink-0 overflow-hidden">
            <Link href={data?.link ? data?.link : "/"} prefetch={false}>
              <div className={`overflow-hidden w-full h-full ${imageVariants[imageStyle]}`}>
                <GumletImage
                  className={`object-cover w-full h-full ${hoverVariants[hoverStyle]}`}
                  imageUrl={data?.image}
                  fallbackSrc="/assets/images/dummy.jpg"
                  alt={data.title}
                  // width={1000}
                  // height={1000}
                  fill
                  loading={priority ? "eager" : "lazy"}
                  fetchPriority={priority ? "high" : "auto"}
                  sizes={sizes}
                />
              </div>
            </Link>
          </div>
        )}
        
        {!hideImage && layout !== "horizontal" && (
          <>
          {data.brand_logo && !hideBrandLogo && (
          <div className={`absolute -top-[0.8px] rounded-b h-14 w-14 left-0 z-10 ${hideBrandLogo ? 'hidden' : ''}`}>
            <GumletImage
              imageUrl={data.brand_logo}
              fallbackSrc="/assets/images/dummy.jpg"
              alt={data.title}
              width={56}
              height={56}
              className=" rounded-br-2xl overflow-hidden"
              loading="lazy"
            />
          </div>
          )}
          <Link className=" block" href={data?.link ? data?.link : "/"} prefetch={false}>
            <div className={`overflow-hidden w-full ${imageVariants[imageStyle]}`}>
              <GumletImage
                className={`h-full object-cover aspect-video w-full ${hoverVariants[hoverStyle]}`}
                imageUrl={data?.image}
                fallbackSrc="/assets/images/dummy.jpg"
                alt={data.title}
                // width={1000}
                // height={1000}
                fill
                loading={priority ? "eager" : "lazy"}
                fetchPriority={priority ? "high" : "auto"}
                sizes={sizes}
              />
            </div>
          </Link>
          </>
        )}

        {/* Play button overlay for video content */}
        {isVideo==="1" && (
          <div className={`absolute inset-0 flex items-center justify-center group-hover:bg-black/20 ${imageVariants[imageStyle]} transition-all pointer-events-none`}>
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
              <Play className="w-5 h-5 text-InkBlack" fill="InkBlack" />
            </div>
          </div>
        )}

        {/* Duration badge - always positioned absolutely within the image container */}
        {showDuration && data.duration !== undefined && (
          <div className={`absolute z-10 ${positionClasses[durationPosition]}`}>
            <TimeBadge time={data.duration} size="md" />
          </div>
        )}

        {/* Overlay text on image if textOnImage is true */}
        {textOnImage && (
          <div className="absolute inset-0 flex flex-col justify-end py-4 px-0 bg-gradient-to-t from-black/60 to-transparent">
            <div className="flex items-center font-inter text-sm text-description font-normal my-3">
              <span className="text-white whitespace-nowrap capitalize">
                <Link href={`/category/${data.categorySlug ? data.categorySlug : "/"}`} prefetch={false}>
                  {data.category ? shortenText(data.category, 10) : "Not Available"}
                </Link>
              </span>
              {data.categorySlug && data.authorSlug && (
                <span className="mx-2 text-white">/</span>
              )}
              <span className="text-white capitalize">
                <Link href={`/author/${data.authorSlug ? data.authorSlug : "/"}`} prefetch={false}>
                  {data.author}
                </Link>
              </span>
            </div>
            <Link href={data?.link ? data?.link : "/"} prefetch={false}>
              <h3
                className={`${titleSizeVariants[titleSize]} ${titleVariants[title]} ${titleMarginVariants[titleMargin]} ${titleLineClampVariants[titleLineClamp]} text-white mb-2`}
              >
                {data.title ? data.title : 'Not Available'}
              </h3>
              {!hideDescription && data.description && (
                <p
                  className={`${descriptionSizeVariants[descriptionSize]} ${descriptionVariants[description]} ${descriptionLineClampVariants[descriptionLineClamp]} font-inter text-white/80`}
                >
                  {data.description ? data.description : "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book"}
                </p>
              )}
            </Link>
          </div>
        )}
      </div>

      {/* Render title and description below image if textOnImage is false */}
      {!textOnImage && layout !== "imageOnly" && (
        <div
          className={` ${
            layout === "horizontal" && data?.image ? "w-full " : hideImage ? "pt-0 pb-4" : "pt-2 pb-4"
          }`}
        >
          <div className={`flex items-center text-xs text-description font-medium ${hideImage ? '!mt-0' : '!mt-2'} mb-2 font-inter line-clamp-1 w-full`}>
            {isVideo==="1" && (
              <span className="flex-shrink-0 mr-2 w-4 h-4 rounded-full bg-inkBlack flex items-center justify-center pointer-events-none">
                <Play className="w-2 h-2 text-background flex-shrink-0" fill="background" />
              </span>
            )}
            <span className="text-inkBlack truncate font-medium capitalize flex-shrink-0">
              <Link href={`/category/${data.categorySlug ? data.categorySlug : "/"}`} className="capitalize truncate block" prefetch={false}>
                {data.category ? data.category : "Not Available"}
              </Link>
            </span>
            <span className="flex-shrink-0 mx-2">/</span>
            <span className="text-inactiveGray font-normal truncate capitalize flex-1 min-w-0">
            <Link href={`/author/${data.authorSlug ? data.authorSlug : "/"}`} prefetch={false}>
            {/* {data.author ? shortenText(data.author,10) : "Not Available"} */}
              {data.author ? data.author : "Not Available"}
            </Link>
            </span>
          </div>
          <Link href={data?.link ? data?.link : "/"} prefetch={false}>
          <h3
            className={`text-inkBlack my-0 -tracking-[1%] ${titleSizeVariants[titleSize]} ${titleVariants[title]} ${titleMarginVariants[titleMargin]} ${titleLineClampVariants[titleLineClamp]}`}
          >
            {data.title ? data.title : "Not  Available"}
          </h3>
            {!hideDescription && (
              <p
                className={`${descriptionSizeVariants[descriptionSize]} ${descriptionVariants[description]} ${descriptionLineClampVariants[descriptionLineClamp]} font-inter text-inkBlack/60`}
              >
                {data.description ? data.description : "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book"}
              </p>
            )}
          </Link>
        </div>
      )}
    </div>
    {/* Video Modal */}
      {/* {isVideo && (
        <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={data.title || "Video"}>
          <div className="aspect-video w-full">
            {data.videoUrl ? (
              <iframe 
                className="w-full h-full"
                src={data.videoUrl}
                title={data.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                <p>Video not available</p>
              </div>
            )}
          </div>
        </Modal>
      )} */}
      </>
  );
};
